import React, { useState, useEffect } from 'react';
import {
    FiSearch,
    FiX,
    FiRefreshCw,
    FiStar,
    FiMessageSquare,
    FiBarChart2
} from 'react-icons/fi';
import { getAllTestimonials, getTestimonialAnalytics } from './testimonialService';
import { getAllGuilds } from '../guilds/guildService';
import { getAllDoctors } from '../doctors/doctorService';
import '../doctors/DoctorList.css';

export default function TestimonialList() {
    const [testimonials, setTestimonials] = useState([]);
    const [filteredTestimonials, setFilteredTestimonials] = useState([]);
    const [guilds, setGuilds] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterGuild, setFilterGuild] = useState('');
    const [filterRating, setFilterRating] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [testimonials, searchTerm, filterGuild, filterRating]);

    async function loadData() {
        try {
            setLoading(true);
            const [testimonialsData, guildsData, doctorsData, analyticsData] = await Promise.all([
                getAllTestimonials(),
                getAllGuilds(),
                getAllDoctors(),
                getTestimonialAnalytics(),
            ]);
            setTestimonials(testimonialsData);
            setGuilds(guildsData);
            setDoctors(doctorsData);
            setAnalytics(analyticsData);
            setError('');
        } catch (err) {
            setError('Failed to load testimonials');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    function applyFilters() {
        let filtered = [...testimonials];

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(t =>
                t.feedback?.toLowerCase().includes(searchLower)
            );
        }

        if (filterGuild) {
            filtered = filtered.filter(t =>
                t.aboutDoctorGuildId === filterGuild || t.byDoctorGuildId === filterGuild
            );
        }

        if (filterRating) {
            filtered = filtered.filter(t => t.rating === parseInt(filterRating));
        }

        setFilteredTestimonials(filtered);
    }

    function getDoctorName(doctorId) {
        const doctor = doctors.find(d => d.id === doctorId);
        return doctor?.fullName || 'Unknown';
    }

    function formatDate(timestamp) {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('en-GB');
    }

    function renderStars(rating) {
        return (
            <div className="star-rating">
                {[1, 2, 3, 4, 5].map(star => (
                    <FiStar
                        key={star}
                        className={star <= rating ? 'star-filled' : 'star-empty'}
                        style={{ color: star <= rating ? '#FFD700' : '#ccc' }}
                    />
                ))}
            </div>
        );
    }

    if (loading) {
        return (
            <div className="page-container">
                <div className="loading-spinner">
                    <FiRefreshCw className="spin-icon" />
                    <p>Loading testimonials...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-left">
                    <h1>Testimonials</h1>
                </div>
            </div>

            {error && (
                <div className="error-message">
                    <FiX />
                    {error}
                </div>
            )}

            {/* Analytics Section */}
            {analytics && (
                <div className="analytics-section">
                    <div className="stat-card">
                        <div className="stat-icon">
                            <FiMessageSquare />
                        </div>
                        <div className="stat-value">{analytics.totalCount}</div>
                        <div className="stat-label">Total Testimonials</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon" style={{ color: '#FF9800', background: '#FFF3E0' }}>
                            <FiStar />
                        </div>
                        <div className="stat-value">{analytics.averageRating}</div>
                        <div className="stat-label">Average Rating</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">
                            <FiBarChart2 />
                        </div>
                        <div className="stat-value" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '1rem' }}>
                            {Object.entries(analytics.ratingDistribution).map(([rating, count]) => (
                                <div key={rating} style={{ fontWeight: 600 }}>
                                    {rating}★: <span style={{ color: 'var(--theme-primary)' }}>{count}</span>
                                </div>
                            ))}
                        </div>
                        <div className="stat-label">Rating Distribution</div>
                    </div>
                </div>
            )
            }

            {/* Filters Section */}
            <div className="filters-section">
                <div className="search-box">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search feedback..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <select
                    value={filterGuild}
                    onChange={(e) => setFilterGuild(e.target.value)}
                    className="filter-select"
                >
                    <option value="">All Guilds</option>
                    {guilds.map(guild => (
                        <option key={guild.id} value={guild.id}>{guild.name}</option>
                    ))}
                </select>

                <select
                    value={filterRating}
                    onChange={(e) => setFilterRating(e.target.value)}
                    className="filter-select"
                >
                    <option value="">All Ratings</option>
                    {[5, 4, 3, 2, 1].map(rating => (
                        <option key={rating} value={rating}>{rating} Stars</option>
                    ))}
                </select>

                {(searchTerm || filterGuild || filterRating) && (
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setFilterGuild('');
                            setFilterRating('');
                        }}
                        className="btn-clear-filters"
                    >
                        <FiX />
                        <span>Clear Filters</span>
                    </button>
                )}
            </div>

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>From</th>
                            <th>To</th>
                            <th>Rating</th>
                            <th>Feedback</th>
                            <th>Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTestimonials.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="empty-state">
                                    <div className="empty-content">
                                        <FiMessageSquare className="empty-icon" />
                                        <p>
                                            {searchTerm || filterGuild || filterRating
                                                ? 'No testimonials match your filters.'
                                                : 'No testimonials found.'}
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredTestimonials.map((testimonial) => (
                                <tr key={testimonial.id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div className="doctor-avatar-circle">
                                                {testimonial.isAnonymous ? '?' : (getDoctorName(testimonial.byDoctorId)[0] || 'D')}
                                            </div>
                                            <span style={{ fontWeight: 500 }}>
                                                {testimonial.isAnonymous
                                                    ? 'Anonymous Doctor'
                                                    : getDoctorName(testimonial.byDoctorId)}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <strong>Dr. {getDoctorName(testimonial.aboutDoctorId)}</strong>
                                            <small style={{ color: '#64748b' }}>Recipient Specialist</small>
                                        </div>
                                    </td>
                                    <td>{renderStars(testimonial.rating)}</td>
                                    <td>
                                        <div className="feedback-preview" title={testimonial.feedback}>
                                            "{testimonial.feedback || 'No feedback provided'}"
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: '0.9rem', color: '#444' }}>
                                            {formatDate(testimonial.createdAt)}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div >
    );
}
